import { cn } from "@/lib/utils";
import { graphlib, Label, layout, configUnion } from "@dagrejs/dagre";
import { useMemo } from "react";
import { Skeleton } from "../skeleton/skeleton";
import { ClockIcon, PlayCircleIcon } from "lucide-react";

type Graph = graphlib.Graph<Model>;

export interface Model {
  id: string;
  name?: string;
  desc?: string;
  active?: boolean;
}

export interface Vertex extends Label, Model {}

export interface Edge extends Label {
  in: string;
  out: string;
}

export interface DAGProps {
  edges: Edge[];
  loading?: boolean;
  layout?: configUnion;
  vertices: Vertex[];
  defaultVertex?: Label;
}

export function DAG({
  edges,
  loading = false,
  vertices,
  defaultVertex = {},
  layout: layoutConfig,
}: DAGProps) {
  const dag = useMemo(() => {
    const g: Graph = new graphlib.Graph({ directed: true });

    g.setGraph({});
    g.setDefaultEdgeLabel(() => ({}));

    for (const { id, ...v } of vertices) {
      g.setNode(id, {
        ...defaultVertex,
        ...v,
        label: v.name || id,
      });
    }

    for (const { in: _in, out, ...e } of edges) {
      g.setEdge(out, _in, e);
    }

    layout(g, layoutConfig);
    return g;
  }, [edges, vertices]);

  const g = dag.graph();
  const h = (Number.isFinite(g.height) ? g.height : 0) ?? 0;
  const w = (Number.isFinite(g.width) ? g.width : 0) ?? 0;

  const renderedVertices = useMemo(() => {
    return dag.nodes().map((id) => {
      const node = dag.node(id);
      const top = node.y - node.height / 2;
      const left = node.x - node.width / 2;
      const active = node.active;
      const Icon = active ? PlayCircleIcon : ClockIcon;

      return (
        <div
          key={id}
          data-testid="vertex"
          className={cn(
            "absolute px-4",
            "flex items-center gap-x-3",
            "border border-input rounded-md bg-card",
            {
              "border-primary": active,
            },
          )}
          style={{
            top: top + "px",
            left: left + "px",
            width: node.width + "px",
            height: node.height + "px",
          }}
        >
          {loading ? (
            <Skeleton className="w-[28px] h-[28px] rounded-full" />
          ) : (
            <Icon
              size={28}
              className={cn(
                {
                  "stroke-border": !active,
                  "stroke-primary": active,
                },
                "flex-shrink-0",
              )}
            />
          )}

          <div className="w-full overflow-hidden flex flex-col gap-y-1">
            <div
              className={cn("text-xs truncate", {
                "text-muted-foreground": !active,
                "text-primary font-bold": active,
              })}
            >
              {loading ? <Skeleton className="w-[50px] h-4" /> : node.name}
            </div>
            <div className="text-sm truncate">
              {loading ? <Skeleton className="w-[150px] h-4" /> : node.desc}
            </div>
          </div>
        </div>
      );
    });
  }, [dag, loading]);

  const renderedEdges = useMemo(() => {
    return dag.edges().map((id, i) => {
      const points = dag.edge(id).points.map((p) => [p.x, p.y].join(","));
      return (
        <polyline
          key={i}
          data-testid="edge"
          points={points.join(" ")}
          fill="none"
          className="stroke-input"
          stroke="currentColor"
          strokeLinecap="round"
          strokeLinejoin="round"
          markerEnd="url(#arrow)"
        />
      );
    });
  }, [dag]);

  return (
    <div
      className="relative"
      style={{
        height: h + "px",
        width: w + "px",
      }}
    >
      {renderedVertices}
      {!loading && (
        <svg
          width={w}
          height={h}
          viewBox={`0 0 ${w} ${h}`}
          xmlns="http://www.w3.org/2000/svg"
          strokeLinejoin="round"
          strokeLinecap="round"
        >
          <defs>
            <marker
              id="arrow"
              viewBox="0 0 14 14"
              refX="14"
              refY="7"
              markerWidth="8"
              markerHeight="8"
              orient="auto-start-reverse"
            >
              <path d="M 0 0 L 14 7 L 0 14 z" className="fill-input" />
            </marker>
          </defs>
          {renderedEdges}
        </svg>
      )}
    </div>
  );
}
